import { inject, Injectable, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { catchError, finalize, from, of, switchMap, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from 'src/environments/environment';
import { SpeechToTextService } from 'src/shared/services/speech-to-text/speech-to-text.service';
import {
  VoiceSynthesisService,
  DEFAULT_ASSISTANT_SPEECH_OPTIONS,
} from 'src/shared/services/voice/voice-synthesis.service';
import { VoiceCommandRegistryService } from './voice-command-registry.service';
import { AssistantMessageRouterService } from '../chat/orchestrator/assistant-message-router.service';
import { AssistantIntentService } from '../chat/orchestrator/assistant-intent.service';
import {
  AssistantWorkflowEngineService,
  type WorkflowTurnResult,
} from '../chat/orchestrator/assistant-workflow-engine.service';
import { AssistantConversationStateService } from '../chat/orchestrator/assistant-conversation-state.service';
import type {
  AssistantAction,
  AssistantChatContext,
  AssistantChatData,
  AssistantState,
  UserRoleHint,
} from '../voice/types/voice-assistant.types';
import {
  finalizeAssistantMessageFromChatData,
  finalizeAssistantMessageFromWorkflowText,
  getSpeechRemainder,
  logAssistantMessageLifecycle,
} from '../voice/utils/assistant-message-finalizer.util';
import {
  readTargetedLang,
  resolveBcp47Lang,
} from 'src/shared/utils/locale-bcp47.util';
import { ASSISTANT_NAVIGATION_ENABLED } from '../voice/assistant-feature-flags';
import { logAssistantDebug } from '../chat/utils/assistant-debug.util';
import { WORKFLOW_SLOT_GENERATION_PATH } from '../chat/models/conversation-state.model';

/** Pause after speech ends → send to backend. */
const SILENCE_SUBMIT_MS = 800;
/** No speech activity → end listen session. */
const IDLE_STOP_MS = 45000;
/** Hard cap on listen session length. */
const MAX_SESSION_MS = 180000;
const MIN_UTTERANCE_CHARS = 3;
/** Safety fallback if TTS state never settles. */
const RESPONSE_IDLE_SAFETY_MS = 30000;

@Injectable({
  providedIn: 'root',
})
export class AssistantOrchestratorService {
  private readonly speech = inject(SpeechToTextService);
  private readonly tts = inject(VoiceSynthesisService);
  private readonly registry = inject(VoiceCommandRegistryService);
  private readonly router = inject(Router);
  private readonly messageRouter = inject(AssistantMessageRouterService);
  private readonly intent = inject(AssistantIntentService);
  private readonly workflowEngine = inject(AssistantWorkflowEngineService);
  private readonly conversationState = inject(
    AssistantConversationStateService
  );
  private readonly translate = inject(TranslateService);

  readonly assistantState = signal<AssistantState>('idle');
  readonly aiReply = signal<string>('');
  /** Mirrors last assistant message content (replay/debug only). */
  readonly lastTtsText = signal<string>('');
  readonly lastError = signal<string | null>(null);
  readonly lastAction = signal<AssistantAction | null>(null);
  /** Combined final + interim for display */
  readonly liveTranscript = signal<string>('');

  /** User-armed mic session (toggle off via second tap or timeout). */
  readonly listenSessionActive = signal(false);

  /** True after first mic tap this sheet open; cleared on resetSession (sheet close). */
  readonly voiceDiscoveryDismissed = signal(false);

  readonly isListening = computed(() => this.listenSessionActive());
  readonly isBusy = computed(() =>
    ['thinking', 'responding'].includes(this.assistantState())
  );

  private silenceSubmitTimer: ReturnType<typeof setTimeout> | null = null;
  private idleStopTimer: ReturnType<typeof setTimeout> | null = null;
  private maxSessionTimer: ReturnType<typeof setTimeout> | null = null;
  private responseIdleTimer: ReturnType<typeof setTimeout> | null = null;
  private bargeInSttTimer: ReturnType<typeof setTimeout> | null = null;
  private lastObservedTranscript = '';
  private isSubmittingFromSilence = false;
  private bargeInWatchActive = false;
  private ttsSpeakingNow = false;
  private wasTtsSpeaking = false;
  /** Set on teacher barge-in; cleared when a new user turn is committed. */
  private ttsSuppressedByBargeIn = false;
  /** STT paused while assistant thinks/speaks during an armed listen session. */
  private listenCapturePaused = false;
  /** During assistant TTS in-session: STT on for barge-in, silence auto-send off. */
  private suppressSilenceSubmitDuringTts = false;
  private activeChatAbort: AbortController | null = null;
  private streamSpokeThisTurn = false;
  private streamPlaybackBegun = false;
  private streamedContentBuffer = '';
  private activeTurnId = 0;
  private wasTtsIdle = true;

  constructor() {
    this.speech
      .getSpeechState()
      .pipe(takeUntilDestroyed())
      .subscribe(s => {
        const live = [s.finalTranscript, s.interimTranscript]
          .filter(Boolean)
          .join(' ')
          .trim();
        this.liveTranscript.set(live);
        if (s.error && this.listenSessionActive()) {
          this.lastError.set(s.error);
          this.assistantState.set('error');
        }
        this.onSpeechStateUpdate(live);
      });

    this.tts
      .getState$()
      .pipe(takeUntilDestroyed())
      .subscribe(s => {
        if (s.speaking && !this.wasTtsSpeaking) {
          this.scheduleBargeInSttActivation();
        }
        const idle = this.tts.isIdle();
        if (idle && !this.wasTtsIdle) {
          this.clearBargeInSttTimer();
          if (this.listenSessionActive()) {
            this.suppressSilenceSubmitDuringTts = false;
            this.resumeListenCaptureAfterAssistantTurn();
          } else {
            this.disablePassiveBargeInWatch();
          }
          this.onAssistantSpeechEnded();
        }
        this.wasTtsSpeaking = s.speaking;
        this.wasTtsIdle = idle;
        this.ttsSpeakingNow = s.speaking;
      });
  }

  buildContext(): AssistantChatContext {
    let teacherId: number | undefined;
    let schoolId: number | undefined;
    let classId: number | undefined;
    let teacherName: string | undefined;
    let role: string | undefined;
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        teacherId = u.teacher_id;
        schoolId = u.school_id;
        classId = u.class_id;
        teacherName = u.teacher_name;
        role = u.role;
      } catch {
        /* ignore */
      }
    }
    return {
      role,
      locale: localStorage.getItem('targetedLang') || 'en',
      routeHint: this.router.url,
      teacherId,
      schoolId,
      classId,
      teacherName,
    };
  }

  startListenSession(): void {
    this.voiceDiscoveryDismissed.set(true);
    this.tts.cancel();
    this.speech.setLanguage(resolveBcp47Lang(readTargetedLang()));
    this.listenCapturePaused = false;
    this.suppressSilenceSubmitDuringTts = false;
    this.lastError.set(null);
    this.speech.clearError();
    this.speech.beginNewUtterance();
    this.liveTranscript.set('');
    this.lastObservedTranscript = '';
    this.isSubmittingFromSilence = false;
    this.clearSessionTimers();
    this.bargeInTeacherPriority(true);
    this.ensureSessionTimers();
  }

  endListenSession(): void {
    this.listenSessionActive.set(false);
    this.listenCapturePaused = false;
    this.suppressSilenceSubmitDuringTts = false;
    this.disablePassiveBargeInWatch();
    this.clearSessionTimers();
    this.clearBargeInSttTimer();
    this.clearResponseIdleTimer();
    this.isSubmittingFromSilence = false;
    this.lastObservedTranscript = '';
    this.stopListening();
  }

  toggleListenSession(): void {
    if (this.listenSessionActive()) {
      this.endListenSession();
      return;
    }
    this.startListenSession();
  }

  startListening(): void {
    this.lastError.set(null);
    this.speech.clearError();
    this.assistantState.set('listening');
    this.speech.setContinuous(true);
    this.speech.startRecording({ freshUtterance: true });
  }

  stopListening(): void {
    this.speech.stopRecording();
    if (
      this.assistantState() === 'listening' ||
      this.assistantState() === 'error'
    ) {
      this.assistantState.set('idle');
    }
  }

  toggleListening(): void {
    this.toggleListenSession();
  }

  /** Process transcript or typed message (local commands first, then AI). */
  processUserMessage(text: string, speak = true): void {
    const trimmed = text.trim();
    if (!trimmed) return;

    logAssistantDebug('LATEST_TRANSCRIPT', { text: trimmed });
    this.conversationState.logActiveWorkflowDebug();

    if (!environment.production) {
      console.info('[assistant] processing started', {
        length: trimmed.length,
      });
    }
    this.conversationState.appendUserMessage(trimmed);

    this.lastError.set(null);
    this.ttsSuppressedByBargeIn = false;
    this.abortActiveChat();
    const turnId = ++this.activeTurnId;
    this.streamSpokeThisTurn = false;
    this.streamPlaybackBegun = false;
    this.streamedContentBuffer = '';
    this.speech.clearTranscript();
    this.liveTranscript.set('');
    this.lastObservedTranscript = '';
    if (this.listenSessionActive()) {
      this.pauseListenCapture();
    }
    this.assistantState.set('thinking');
    this.aiReply.set('');
    this.lastTtsText.set('');

    const role = this.inferRole();
    const useStream = !this.conversationState.workflowInProgress();
    this.activeChatAbort = new AbortController();
    const chatSignal = this.activeChatAbort.signal;

    this.registry
      .tryLocalCommand(trimmed, role)
      .pipe(
        take(1),
        switchMap(local => {
          if (local) {
            return of({
              response: local.replySpoken,
              speechText: local.replySpoken,
              action: local.action,
              metadata: local.metadata ?? {},
            } as AssistantChatData);
          }
          return this.intent.classify(trimmed, false).pipe(
            take(1),
            switchMap(intent => {
              const wfDebug =
                this.conversationState.getActiveWorkflowDebugState();
              logAssistantDebug('DETECTED_INTENT', {
                kind: intent.kind,
                artifactHint: intent.artifactHint,
              });
              logAssistantDebug('ROUTE_TAKEN', {
                userInput: trimmed,
                activeWorkflow: wfDebug.workflowId,
                currentStep: wfDebug.currentStep,
                workflowStatus: wfDebug.workflowStatus,
                intent: intent.kind,
                artifactHint: intent.artifactHint,
              });

              if (intent.kind === 'session_end') {
                logAssistantDebug('ACTION_EXECUTED', { action: 'session_end' });
                return this.messageRouter.route(trimmed, () =>
                  this.buildContext()
                );
              }

              if (intent.kind === 'workflow_continue') {
                logAssistantDebug('ACTION_EXECUTED', {
                  action: 'workflow_continue',
                });
                return this.messageRouter.routeWorkflowContinue(
                  trimmed,
                  intent.workflowEnrichment
                );
              }

              if (intent.kind === 'general_chat') {
                if (this.shouldTreatGeneralChatAsWorkflowContinue()) {
                  logAssistantDebug('ACTION_EXECUTED', {
                    action: 'general_chat_as_workflow_continue',
                  });
                  return this.messageRouter.routeWorkflowContinue(trimmed);
                }
                logAssistantDebug('ACTION_EXECUTED', {
                  action: 'general_chat_stream',
                });
                this.messageRouter.resetActiveWorkflow();
                return from(
                  this.messageRouter
                    .chatStream(
                      () => this.buildContext(),
                      trimmed,
                      evt => {
                        if (turnId !== this.activeTurnId) return;
                        if (
                          evt.type === 'sentence' &&
                          speak &&
                          this.shouldPlayAssistantTts()
                        ) {
                          this.streamSpokeThisTurn = true;
                          this.streamedContentBuffer = [
                            this.streamedContentBuffer,
                            evt.text,
                          ]
                            .filter(Boolean)
                            .join(' ')
                            .trim();
                          if (!this.streamPlaybackBegun) {
                            this.tts.beginReplyPlayback();
                            this.streamPlaybackBegun = true;
                          }
                          if (this.assistantState() === 'thinking') {
                            this.assistantState.set('responding');
                          }
                          this.tts.speakMessageContent(
                            evt.text,
                            turnId,
                            DEFAULT_ASSISTANT_SPEECH_OPTIONS
                          );
                        }
                      },
                      chatSignal
                    )
                    .catch(err => {
                      if (err?.name === 'AbortError') {
                        throw err;
                      }
                      console.warn(
                        '[assistant] stream failed, falling back to /chat',
                        err
                      );
                      return this.messageRouter
                        .exitWorkflowAndChat(trimmed, () => this.buildContext())
                        .pipe(take(1))
                        .toPromise();
                    })
                );
              }

              if (this.messageRouter.requiresWorkflowRoute(intent)) {
                logAssistantDebug('ACTION_EXECUTED', {
                  action: 'requiresWorkflowRoute',
                  kind: intent.kind,
                });
                return this.messageRouter.route(trimmed, () =>
                  this.buildContext()
                );
              }

              if (useStream) {
                return from(
                  this.messageRouter
                    .chatStream(
                      () => this.buildContext(),
                      trimmed,
                      evt => {
                        if (turnId !== this.activeTurnId) return;
                        if (
                          evt.type === 'sentence' &&
                          speak &&
                          this.shouldPlayAssistantTts()
                        ) {
                          this.streamSpokeThisTurn = true;
                          this.streamedContentBuffer = [
                            this.streamedContentBuffer,
                            evt.text,
                          ]
                            .filter(Boolean)
                            .join(' ')
                            .trim();
                          if (!this.streamPlaybackBegun) {
                            this.tts.beginReplyPlayback();
                            this.streamPlaybackBegun = true;
                          }
                          if (this.assistantState() === 'thinking') {
                            this.assistantState.set('responding');
                          }
                          this.tts.speakMessageContent(
                            evt.text,
                            turnId,
                            DEFAULT_ASSISTANT_SPEECH_OPTIONS
                          );
                        }
                      },
                      chatSignal
                    )
                    .catch(err => {
                      if (err?.name === 'AbortError') {
                        throw err;
                      }
                      console.warn(
                        '[assistant] stream failed, falling back to /chat',
                        err
                      );
                      return this.messageRouter
                        .chatGeneral(trimmed, () => this.buildContext())
                        .pipe(take(1))
                        .toPromise();
                    })
                );
              }
              return this.messageRouter.route(trimmed, () =>
                this.buildContext()
              );
            })
          );
        }),
        catchError(err => {
          if (err?.name === 'AbortError') {
            return of(null);
          }
          console.error('[AssistantOrchestrator] message pipeline failed', err);
          const msg = this.resolveChatErrorMessage(err);
          this.lastError.set(msg);
          return of({
            response: msg,
            action: null,
            metadata: {},
          } as AssistantChatData);
        })
      )
      .pipe(
        finalize(() => {
          this.isSubmittingFromSilence = false;
        })
      )
      .subscribe(data => {
        if (!data || turnId !== this.activeTurnId) return;
        this.activeChatAbort = null;

        const isWorkflow = !!data.metadata?.['workflow'];
        let speakContent: string;

        if (isWorkflow) {
          speakContent = this.commitWorkflowAssistantReply(data);
        } else {
          const finalized = finalizeAssistantMessageFromChatData(data);
          speakContent = finalized.content;
          this.conversationState.appendFinalizedAssistantMessage(finalized);
        }

        this.aiReply.set(speakContent);
        this.lastTtsText.set(speakContent);
        this.lastAction.set(
          ASSISTANT_NAVIGATION_ENABLED ? (data.action ?? null) : null
        );
        if (ASSISTANT_NAVIGATION_ENABLED) {
          this.executeAction(data.action);
        }

        logAssistantMessageLifecycle('UI_RENDERED', {
          turnId,
          contentLength: speakContent.length,
        });

        const remainder = this.streamSpokeThisTurn
          ? getSpeechRemainder(speakContent, this.streamedContentBuffer)
          : null;
        const willSpeakFull =
          speak &&
          !!speakContent &&
          this.shouldPlayAssistantTts() &&
          !this.streamSpokeThisTurn;
        const willSpeakRemainder =
          speak &&
          !!remainder &&
          this.shouldPlayAssistantTts() &&
          this.streamSpokeThisTurn;

        if (this.streamSpokeThisTurn && !willSpeakRemainder) {
          this.tts.finishReplyPlayback();
          this.streamPlaybackBegun = false;
        }

        if (this.streamSpokeThisTurn || willSpeakFull || willSpeakRemainder) {
          this.assistantState.set('responding');
        } else if (this.listenSessionActive()) {
          this.assistantState.set('listening');
          this.resumeListenCaptureAfterAssistantTurn();
        } else {
          this.assistantState.set('idle');
        }

        if (willSpeakFull) {
          this.tts.speakMessageContent(
            speakContent,
            turnId,
            DEFAULT_ASSISTANT_SPEECH_OPTIONS
          );
        } else if (willSpeakRemainder) {
          this.tts.speakMessageContent(
            remainder!,
            turnId,
            DEFAULT_ASSISTANT_SPEECH_OPTIONS
          );
          this.tts.finishReplyPlayback();
          this.streamPlaybackBegun = false;
        } else if (
          this.listenSessionActive() &&
          this.tts.isIdle() &&
          !this.streamSpokeThisTurn
        ) {
          this.resumeListenCaptureAfterAssistantTurn();
        }

        this.streamedContentBuffer = '';
        this.scheduleResponseIdleSafety();
      });
  }

  /** Educational tool tile / explicit artifact pick — voice-first Quick vs Custom. */
  bootstrapEducationalArtifact(artifact: string, speak = true): void {
    const label = this.translate.instant(
      `virtualAssistant.artifacts.${artifact}`
    );
    this.conversationState.appendUserMessage(label);
    this.lastError.set(null);
    this.ttsSuppressedByBargeIn = false;
    this.abortActiveChat();
    const turnId = ++this.activeTurnId;
    this.streamSpokeThisTurn = false;
    this.streamPlaybackBegun = false;
    this.streamedContentBuffer = '';
    if (this.listenSessionActive()) {
      this.pauseListenCapture();
    }
    this.assistantState.set('thinking');
    this.aiReply.set('');
    this.lastTtsText.set('');

    this.messageRouter
      .bootstrapEducationalArtifact(artifact)
      .pipe(take(1))
      .subscribe(data => {
        if (turnId !== this.activeTurnId) return;
        this.isSubmittingFromSilence = false;
        this.activeChatAbort = null;

        const lastContent =
          this.conversationState.getLastAssistantSpeakableContent();
        let speakContent: string;
        speakContent = this.commitWorkflowAssistantReply(data);
        if (!speakContent && lastContent) {
          speakContent = lastContent;
        }

        this.aiReply.set(speakContent);
        this.lastTtsText.set(speakContent);
        this.lastAction.set(null);
        this.assistantState.set('responding');

        if (speak && speakContent && this.shouldPlayAssistantTts()) {
          this.tts.speakMessageContent(
            speakContent,
            turnId,
            DEFAULT_ASSISTANT_SPEECH_OPTIONS
          );
        } else if (this.listenSessionActive()) {
          this.assistantState.set('listening');
          this.resumeListenCaptureAfterAssistantTurn();
        } else {
          this.assistantState.set('idle');
        }
        this.scheduleResponseIdleSafety();
      });
  }

  /** Quick-reply chip during educational hybrid workflow. */
  processWorkflowChip(
    slotKey: string,
    value: string,
    speak = true,
    userLabel?: string
  ): void {
    const userText = (userLabel?.trim() || value).trim();
    if (userText) {
      this.conversationState.appendUserMessage(userText);
    }
    this.lastError.set(null);
    this.ttsSuppressedByBargeIn = false;
    if (this.listenSessionActive()) {
      this.pauseListenCapture();
    }
    const turnId = ++this.activeTurnId;
    this.assistantState.set('thinking');
    this.aiReply.set('');
    this.lastTtsText.set('');
    const turn = this.workflowEngine.applyChipSelection(slotKey, value);
    this.lastAction.set(null);
    this.assistantState.set('responding');

    const commitWorkflowTurn = (response: string, turnSpeak: boolean) => {
      if (turnId !== this.activeTurnId) return;
      const speakContent = this.commitWorkflowAssistantReply({
        response,
        action: null,
        metadata: { workflow: true },
      });
      this.aiReply.set(speakContent);
      this.lastTtsText.set(speakContent);
      if (turnSpeak && speakContent && this.shouldPlayAssistantTts()) {
        this.tts.speakMessageContent(
          speakContent,
          turnId,
          DEFAULT_ASSISTANT_SPEECH_OPTIONS
        );
      } else if (this.listenSessionActive()) {
        this.resumeListenCaptureAfterAssistantTurn();
      }
      this.scheduleResponseIdleSafety();
    };

    if (turn.followUp$) {
      turn.followUp$
        .pipe(
          take(1),
          catchError(() =>
            of<WorkflowTurnResult>({
              response: this.translate.instant('toast.genericError'),
              speak: true,
            })
          )
        )
        .subscribe(t => commitWorkflowTurn(t.response, speak && t.speak));
    } else {
      commitWorkflowTurn(turn.response, speak && turn.speak);
    }
  }

  replayLastReply(): void {
    const t = this.conversationState.getLastAssistantSpeakableContent();
    if (t) {
      this.tts.speakMessageContent(
        t,
        undefined,
        DEFAULT_ASSISTANT_SPEECH_OPTIONS
      );
    }
  }

  cancelSpeech(): void {
    this.tts.cancel();
  }

  resetSession(): void {
    this.abortActiveChat();
    this.workflowEngine.endSession();
    this.conversationState.resetEducationalSession();
    this.voiceDiscoveryDismissed.set(false);
    this.clearBargeInSttTimer();
    this.endListenSession();
    this.speech.beginNewUtterance();
    this.liveTranscript.set('');
    this.aiReply.set('');
    this.lastTtsText.set('');
    this.lastAction.set(null);
    this.lastError.set(null);
    this.assistantState.set('idle');
    this.tts.cancel();
    this.conversationState.clearGeneratedEducationalView();
    this.conversationState.clearChatMessages();
  }

  private onSpeechStateUpdate(live: string): void {
    if (live === this.lastObservedTranscript) {
      return;
    }

    const trimmed = live.trim();
    const hasUtterance = trimmed.length >= MIN_UTTERANCE_CHARS;

    // Advance before side effects — clearError() re-enters this handler synchronously.
    this.lastObservedTranscript = live;

    if (hasUtterance && this.shouldBargeInOnTeacherSpeech()) {
      this.bargeInTeacherPriority(true);
    }

    if (!this.listenSessionActive()) {
      return;
    }

    if (this.canScheduleTeacherCapture()) {
      this.scheduleIdleStopTimer();
      if (hasUtterance) {
        this.scheduleSilenceSubmit();
      }
    }
  }

  private shouldBargeInOnTeacherSpeech(): boolean {
    const state = this.assistantState();
    // While capture is paused for an in-flight API turn, ignore stale STT finals.
    if (state === 'thinking' && this.listenCapturePaused) {
      return false;
    }
    return (
      this.bargeInWatchActive ||
      this.ttsSpeakingNow ||
      state === 'responding' ||
      state === 'thinking'
    );
  }

  private bargeInTeacherPriority(armSession: boolean): void {
    this.ttsSuppressedByBargeIn = true;
    this.abortActiveChat();
    ++this.activeTurnId;
    this.listenCapturePaused = false;
    this.suppressSilenceSubmitDuringTts = false;
    this.clearResponseIdleTimer();
    this.interruptAssistantSpeech();
    this.bargeInWatchActive = false;

    if (armSession) {
      this.listenSessionActive.set(true);
      this.ensureSessionTimers();
    }

    this.lastError.set(null);
    const snap = this.speech.getSnapshot();
    if (snap.error || snap.errorCode) {
      this.speech.clearError();
    }
    this.assistantState.set('listening');
    this.speech.setContinuous(true);
    if (!this.speech.getSnapshot().isRecording) {
      this.speech.startRecording();
    }
    if (this.listenSessionActive()) {
      this.scheduleIdleStopTimer();
    }
  }

  private disablePassiveBargeInWatch(): void {
    this.bargeInWatchActive = false;
    if (!this.listenSessionActive()) {
      this.speech.stopRecording();
    }
  }

  private ensureSessionTimers(): void {
    if (!this.maxSessionTimer) {
      this.maxSessionTimer = setTimeout(() => {
        this.maxSessionTimer = null;
        if (this.listenSessionActive()) {
          this.endListenSession();
        }
      }, MAX_SESSION_MS);
    }
  }

  private scheduleResponseIdleSafety(): void {
    this.clearResponseIdleTimer();
    this.responseIdleTimer = setTimeout(() => {
      this.responseIdleTimer = null;
      this.onAssistantSpeechEnded();
    }, RESPONSE_IDLE_SAFETY_MS);
  }

  private onAssistantSpeechEnded(): void {
    if (!this.tts.isIdle()) return;
    this.clearResponseIdleTimer();
    if (this.assistantState() === 'responding') {
      if (this.listenSessionActive()) {
        this.assistantState.set('listening');
        this.resumeListenCaptureAfterAssistantTurn();
      } else {
        this.assistantState.set('idle');
      }
    }
  }

  private abortActiveChat(): void {
    if (this.activeChatAbort) {
      this.activeChatAbort.abort();
      this.activeChatAbort = null;
    }
  }

  private scheduleResponseIdleTransition(): void {
    this.scheduleResponseIdleSafety();
  }

  private clearResponseIdleTimer(): void {
    if (this.responseIdleTimer) {
      clearTimeout(this.responseIdleTimer);
      this.responseIdleTimer = null;
    }
  }

  private interruptAssistantSpeech(): void {
    this.clearBargeInSttTimer();
    this.tts.cancel();
  }

  /** Blocks automatic reply TTS only after teacher barge-in on this turn. */
  private shouldPlayAssistantTts(): boolean {
    return !this.ttsSuppressedByBargeIn;
  }

  private isAssistantOccupied(): boolean {
    const state = this.assistantState();
    return (
      this.ttsSpeakingNow || state === 'thinking' || state === 'responding'
    );
  }

  private canScheduleTeacherCapture(): boolean {
    return (
      this.listenSessionActive() &&
      !this.listenCapturePaused &&
      !this.isAssistantOccupied() &&
      !this.suppressSilenceSubmitDuringTts
    );
  }

  private pauseListenCapture(): void {
    if (!this.listenSessionActive()) return;
    this.listenCapturePaused = true;
    this.clearSilenceSubmitTimer();
    this.clearIdleStopTimer();
    this.speech.stopRecording();
  }

  /**
   * Disabled: arming the mic while TTS audio is playing lets the recognizer
   * pick up the assistant's own voice (no echo cancellation on most devices),
   * which self-triggers barge-in and spirals into an endless cancel/re-speak
   * loop. Mic capture still resumes normally once TTS goes idle.
   */
  private scheduleBargeInSttActivation(): void {
    this.clearBargeInSttTimer();
  }

  private clearBargeInSttTimer(): void {
    if (this.bargeInSttTimer) {
      clearTimeout(this.bargeInSttTimer);
      this.bargeInSttTimer = null;
    }
  }

  private resumeListenCaptureAfterAssistantTurn(): void {
    if (!this.listenSessionActive()) return;
    if (this.isAssistantOccupied()) return;
    this.listenCapturePaused = false;
    this.suppressSilenceSubmitDuringTts = false;
    this.assistantState.set('listening');
    this.speech.setContinuous(true);
    this.liveTranscript.set('');
    this.lastObservedTranscript = '';
    if (!this.speech.getSnapshot().isRecording) {
      this.speech.startRecording({ freshUtterance: true });
    }
    this.scheduleIdleStopTimer();
  }

  private scheduleSilenceSubmit(): void {
    if (!this.canScheduleTeacherCapture()) return;
    if (this.silenceSubmitTimer) {
      clearTimeout(this.silenceSubmitTimer);
    }
    this.silenceSubmitTimer = setTimeout(() => {
      this.silenceSubmitTimer = null;
      this.trySilenceSubmit();
    }, SILENCE_SUBMIT_MS);
  }

  private scheduleIdleStopTimer(): void {
    if (!this.canScheduleTeacherCapture()) return;
    if (this.idleStopTimer) {
      clearTimeout(this.idleStopTimer);
    }
    this.idleStopTimer = setTimeout(() => {
      this.idleStopTimer = null;
      if (this.listenSessionActive() && this.canScheduleTeacherCapture()) {
        this.endListenSession();
      }
    }, IDLE_STOP_MS);
  }

  private trySilenceSubmit(): void {
    if (
      !this.listenSessionActive() ||
      this.isSubmittingFromSilence ||
      this.isBusy() ||
      this.listenCapturePaused ||
      this.suppressSilenceSubmitDuringTts
    ) {
      return;
    }
    const text = this.speech.commitInterimToFinal().trim();
    if (text.length < MIN_UTTERANCE_CHARS) {
      return;
    }
    this.isSubmittingFromSilence = true;
    this.speech.clearTranscript();
    this.liveTranscript.set('');
    this.lastObservedTranscript = '';
    this.processUserMessage(text, true);
  }

  private clearSilenceSubmitTimer(): void {
    if (this.silenceSubmitTimer) {
      clearTimeout(this.silenceSubmitTimer);
      this.silenceSubmitTimer = null;
    }
  }

  private clearIdleStopTimer(): void {
    if (this.idleStopTimer) {
      clearTimeout(this.idleStopTimer);
      this.idleStopTimer = null;
    }
  }

  private clearSessionTimers(): void {
    this.clearSilenceSubmitTimer();
    this.clearIdleStopTimer();
    if (this.maxSessionTimer) {
      clearTimeout(this.maxSessionTimer);
      this.maxSessionTimer = null;
    }
  }

  private commitWorkflowAssistantReply(data: AssistantChatData): string {
    const text = (data.speechText ?? data.response ?? '').trim();
    if (!text) {
      return this.conversationState.getLastAssistantSpeakableContent() ?? '';
    }
    const lastContent =
      this.conversationState.getLastAssistantSpeakableContent();
    if (lastContent !== text) {
      const finalized = finalizeAssistantMessageFromWorkflowText(text);
      this.conversationState.appendFinalizedAssistantMessage(finalized);
    }
    return text;
  }

  /** Safety net: mid-flow slot answers must not wipe workflow on misclassified general chat. */
  private shouldTreatGeneralChatAsWorkflowContinue(): boolean {
    if (!this.conversationState.workflowInProgress()) {
      return false;
    }
    const pathMode = this.conversationState.generationPathMode();
    if (pathMode !== 'custom' && pathMode !== 'quick') {
      return false;
    }
    const slot = this.conversationState.currentSlotKey();
    return !!slot && slot !== WORKFLOW_SLOT_GENERATION_PATH;
  }

  private inferRole(): UserRoleHint {
    try {
      const u = JSON.parse(
        (typeof window !== 'undefined' && localStorage.getItem('user')) || '{}'
      );
      const r = String(u.role || '').toLowerCase();
      if (r.includes('principal')) return 'principal';
      if (r.includes('admin')) return 'admin';
    } catch {
      /* ignore */
    }
    return 'teacher';
  }

  private resolveChatErrorMessage(err: unknown): string {
    const status = (err as { status?: number })?.status;
    const message = String((err as Error)?.message ?? err ?? '').toLowerCase();
    if (
      status === 429 ||
      message.includes('quota') ||
      message.includes('rate limit') ||
      message.includes('resource exhausted')
    ) {
      return this.translate.instant('voiceAssistant.chat.quotaExceeded');
    }
    return this.translate.instant('toast.genericError');
  }

  executeAction(action: AssistantAction | null): void {
    if (!ASSISTANT_NAVIGATION_ENABLED || !action?.target) return;
    const t = action.target;
    const p = action.params ?? {};

    if (t === '/class-room') {
      const cid = Number(p['classId']);
      const tn = String(p['teacherName'] || '');
      if (cid && tn) {
        void this.router.navigate(['/class-room', cid, tn]);
      }
      return;
    }
    if (t === '/lesson-plan') {
      const cid = Number(p['classId']);
      const sectionId = Number(p['sectionId']);
      if (cid) {
        const classLevelId = Number(p['classLevelId']);
        void this.router.navigate(['/lesson-plan', cid], {
          queryParams: {
            class_level_id: classLevelId > 0 ? classLevelId : cid,
            ...(sectionId > 0 ? { section_id: sectionId } : {}),
          },
        });
      }
      return;
    }
    if (t === '/connect-with-parents') {
      const cid = Number(p['classId']);
      if (cid) void this.router.navigate(['/connect-with-parents', cid]);
      return;
    }
    if (t === '/connect-with-parents-v2') {
      const tid = Number(p['teacherId']);
      if (tid) void this.router.navigate(['/connect-with-parents-v2', tid]);
      return;
    }
    if (t === '/teacher/assessment') {
      const sid = Number(p['schoolId']);
      if (sid) void this.router.navigate(['/teacher/assessment', sid]);
      return;
    }

    if (t.startsWith('/')) {
      void this.router.navigateByUrl(t);
    }
  }
}
